import { SubRouteAttributes } from '@albatrosdeveloper/ave-models-package';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { CalendarPeriodicityComponent } from 'app/modules/components/routing/sub-routes/sub-route-periodicity/calendar-periodicity/calendar-periodicity.component';
import { CustomDatesModalComponent } from 'app/modules/components/routing/sub-routes/sub-route-periodicity/modal-w-frequencies/modal-w-frequencies.component';
import { CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { BusinessPartnerBySubRoute } from '../../../../interfaces/busines-partner-by-sub-route.interface';

/**
 * Cell renderer params with frequency-specific properties
 */
interface FrequencyCellRendererParams extends CellRendererParams<BusinessPartnerBySubRoute> {
  getSubRouteId: () => string | null;
  getSubRouteData: () => SubRouteAttributes | null;
  onRefresh: () => void;
}

@Component({
  selector: 'app-frequency-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, TranslatePipe],
  template: `
    <div class="flex items-center gap-2">
      <button
        mat-icon-button
        class="!h-8 !w-8"
        (click)="openCalendarModal()"
        [matTooltip]="'routing.SUB_ROUTE_PARTNERS.CELLS.FREQUENCY_CALENDAR_TOOLTIP' | t"
      >
        <mat-icon class="!text-lg" svgIcon="heroicons_outline:calendar-days"></mat-icon>
      </button>
      <div class="flex flex-col">
        <span class="min-h-[24px] text-sm">{{ daysText() || '-' }}</span>
        <div class="flex items-baseline gap-2">
          <span class="text-xs text-gray-600" [matTooltip]="'routing.SUB_ROUTE_PARTNERS.CELLS.FREQUENCY_PER_DAY_TOOLTIP' | t">
            D({{ dayFrequenciesCount() }})
          </span>
          <button
            mat-button
            color="primary"
            class="!min-w-0 !p-0 !leading-none"
            [disabled]="customFrequenciesCount() === 0"
            (click)="openCustomFrequenciesModal()"
            [matTooltip]="'routing.SUB_ROUTE_PARTNERS.CELLS.FREQUENCY_EVENTS_AGENDA_TOOLTIP' | t"
          >
            A({{ customFrequenciesCount() }})
          </button>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrequencyCellRendererComponent {
  private dialog = inject(MatDialog);

  readonly params = input.required<FrequencyCellRendererParams>();

  readonly data = computed(() => this.params()?.data);

  // From cellRendererParams - use getters for reactive values
  readonly onRefresh = computed(() => this.params()?.onRefresh);

  // Get subRouteId by calling the getter function
  private getSubRouteId(): string | null {
    return this.params()?.getSubRouteId?.() ?? null;
  }

  // Get subRouteData by calling the getter function
  private getSubRouteData(): SubRouteAttributes | null {
    return this.params()?.getSubRouteData?.() ?? null;
  }

  // Frequency computed values
  readonly daysText = computed(() => this.data()?.bpDaysInAllFrecuencies ?? null);
  readonly dayFrequenciesCount = computed(() => this.data()?.bpDayFrecuenciesLength ?? 0);
  readonly customFrequenciesCount = computed(() => this.data()?.bpCustomFrecuenciesLength ?? 0);

  openCalendarModal(): void {
    const bpData = this.data();
    const subRouteId = this.getSubRouteId();
    const refreshCallback = this.onRefresh();
    if (!bpData || !subRouteId) return;

    const dialogRef = this.dialog.open(CalendarPeriodicityComponent, {
      data: {
        subRouteId: subRouteId,
        businessPartners: [bpData],
        frecuencySelected: null,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        refreshCallback?.();
      }
    });
  }

  openCustomFrequenciesModal(): void {
    const bpData = this.data();
    const subRouteData = this.getSubRouteData();
    if (!bpData || this.customFrequenciesCount() === 0) return;

    this.dialog.open(CustomDatesModalComponent, {
      data: {
        frequencies: bpData,
        subRoute: subRouteData,
      },
    });
  }
}
